import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Home } from "lucide-react";
import api from "../lib/api";
import { JobCard } from "../components/JobCard";
import { AdCard } from "../components/AdCard";
import { CategoryChips } from "../components/CategoryChips";
import { AdSlot } from "../components/AdSense";
import { CATEGORIES } from "../config";
import { useAuth } from "../context/AuthContext";

export default function Jobs() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [ads, setAds] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState("all");
  const [housingFirst, setHousingFirst] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/jobs").then(({ data }) => setJobs(data || [])),
      api.get("/ads").then(({ data }) => setAds((data || []).filter((a) => a.placement === "feed"))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.role === "candidate") {
      api.get("/candidate/saved").then(({ data }) => {
        const ids = (data || []).map((j) => j.id || j.job_id);
        setSavedIds(ids);
      }).catch(() => {});
    }
  }, [user]);

  const filtered = useMemo(() => {
    let list = [...jobs];
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (j) =>
          j.title?.toLowerCase().includes(s) ||
          j.restaurant?.name?.toLowerCase().includes(s) ||
          j.city?.toLowerCase().includes(s) ||
          j.position_type?.toLowerCase().includes(s)
      );
    }
    if (category !== "all") {
      const cat = CATEGORIES.find((c) => c.key === category);
      list = list.filter((j) => {
        const hay = `${j.title} ${j.position_type}`.toLowerCase();
        return cat?.match.some((m) => hay.includes(m));
      });
    }
    list.sort((a, b) => {
      if (housingFirst && a.offers_housing !== b.offers_housing) return a.offers_housing ? -1 : 1;
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [jobs, q, category, housingFirst]);

  const onSearch = (e) => {
    setQ(e.target.value);
    setParams(e.target.value ? { q: e.target.value } : {});
  };

  const onToggleSaved = (id, saved) =>
    setSavedIds((prev) => (saved ? [...prev, id] : prev.filter((x) => x !== id)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-stone-text mb-6">{t("jobs.title")}</h1>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-muted" />
        <input
          data-testid="jobs-search-input"
          value={q}
          onChange={onSearch}
          placeholder={t("jobs.searchPlaceholder")}
          className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white border border-stone-border text-stone-text placeholder:text-stone-muted focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </div>

      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <CategoryChips active={category} onChange={setCategory} />
        <button
          data-testid="housing-first-toggle"
          onClick={() => setHousingFirst((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium border transition-colors ${
            housingFirst ? "bg-housing text-white border-housing" : "bg-white text-stone-sub border-stone-border"
          }`}
        >
          <Home className="w-4 h-4" /> {t("jobs.housingFirst")}
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-stone-muted" data-testid="jobs-loading">{t("jobs.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-stone-muted" data-testid="jobs-empty">{t("jobs.noResults")}</div>
      ) : (
        <>
          <p className="text-sm text-stone-muted mb-4" data-testid="jobs-count">
            {filtered.length} {t("jobs.results")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((job, i) => (
              <React.Fragment key={job.id}>
                <JobCard job={job} saved={savedIds.includes(job.id)} onToggleSaved={onToggleSaved} />
                {ads.length > 0 && i === 2 && <AdCard ad={ads[0]} />}
              </React.Fragment>
            ))}
          </div>
          <AdSlot className="mt-8" />
        </>
      )}
    </div>
  );
}
